import { Download } from '@mui/icons-material';
import { Box, IconButton, Tooltip } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useMemo, useState } from 'react';
import { RtVueApiRoutes } from 'RtExports/routes';
import {
	FileLogIndexRequest,
	FileLogProfileResponse,
	FileLogTotalsIndexRequest,
	FileLogTotalsIndexResponse,
	GuardianFileLog
} from 'RtModels';
import { CustomFileLogButton } from 'RtUi/app/rtVue/FileLog/lib/components/CustomFileLogButton';
import { FileLogResource } from 'RtUi/app/rtVue/FileLog/lib/resources/FileLogResource';
import { useGetFileLogs } from 'RtUi/app/rtVue/FileLog/lib/service';
import DataGrid from 'RtUi/components/data/DataGrid';
import { getFileLogsColumns } from 'RtUi/components/data/DataGrid/configurations/rtVue/fileLogs';
import { Confirmation } from 'RtUi/components/form/Confirmation';
import { RtxPageSizes } from 'RtUi/RtxUiConstants';
import { FileUtils } from 'RtUi/utils/file/FileUtils';
import {
	FullResponse,
	handleGetRequest
} from 'RtUi/utils/http/AxiosHttpRequest';

interface IFileLogGridProps<T extends FileLogRequest> {
	resourceParams?: T;
}

export type FileLogRequest = FileLogIndexRequest | FileLogTotalsIndexRequest;
export type FileLogResponse<T extends FileLogRequest> =
	T extends FileLogIndexRequest ? GuardianFileLog : FileLogTotalsIndexResponse;

export const FileLogGrid = <T extends FileLogRequest>({
	resourceParams
}: IFileLogGridProps<T>) => {
	const [rowSelection, setRowSelection] = useState<Array<FileLogResponse<T>>>(
		[]
	);
	const {
		data: fileLogData,
		isFetching: isLoading,
		refetch
	} = useGetFileLogs<T>(resourceParams);

	const fetchFileLogs = async (
		queryParams?: FileLogIndexRequest
	): Promise<FullResponse<GuardianFileLog[]>> => {
		return handleGetRequest(RtVueApiRoutes.FileLogs.Index, {
			queryParams,
			includeFullResponse: true
		});
	};

	const FullResultsReq = async () => {
		const params = { ...resourceParams, pageSize: RtxPageSizes.ExcelMax };
		const data = (await fetchFileLogs(params)) as any;
		return data.data;
	};

	const isFileLogTotals = useMemo(
		() => (resourceParams?.fileStreamIds ?? []).length > 1,
		[resourceParams]
	);

	const columns = useMemo(
		() => getFileLogsColumns<T>(isFileLogTotals),
		[isFileLogTotals]
	);

	const renderCustomButton = () => {
		if (isEmpty(rowSelection)) {
			return <></>;
		}

		return (
			<CustomFileLogButton
				selected={rowSelection}
				refetch={refetch}
				setRowSelected={setRowSelection}
			/>
		);
	};

	const fileUtils = new FileUtils();

	const downloadFile = async (fileLogId: number) => {
		const res = (await new FileLogResource().getProfile(
			fileLogId
		)) as FileLogProfileResponse;

		if (!res.signedUrl) {
			alert('File not found');
		}

		const confirm = await Confirmation.create(
			`Are you sure you want to download file: ${res.fileName}`
		);
		if (!confirm) {
			return;
		}

		return fileUtils.downloadFromUrlWithPromise(
			Promise.resolve({
				url: res.signedUrl,
				fileName: res.fileName
			})
		);
	};

	const inlineActions = (row: FileLogResponse<T>) => (
		<Box sx={{ display: 'flex', justifyContent: 'center' }}>
			<Tooltip title="Download File" arrow>
				<IconButton
					type="button"
					color="info"
					size="small"
					onClick={(e) => {
						e.preventDefault();
						downloadFile(row.fileId);
					}}
					sx={{
						padding: 0
					}}
				>
					<Download />
				</IconButton>
			</Tooltip>
		</Box>
	);

	return (
		<DataGrid<FileLogResponse<T>>
			data={fileLogData?.data}
			columns={columns}
			pageName={'configureRateSheet-fileLog'}
			enableExternalExport
			getExternalExportData={FullResultsReq}
			loading={isLoading}
			headerAction={renderCustomButton}
			inlineActions={inlineActions}
			positionActionsColumn="first"
			enableRowSelection={!isFileLogTotals}
			selectedRows={rowSelection}
			onChangeSelectedRows={setRowSelection}
			totalRows={fileLogData?.totalCount}
			disableExternalLinks
		/>
	);
};
